import './css/main.css';

interface KeyValueOption {
    Label: string;
    Value: string;
}

interface HubSpotKeyValueOptions {
    Options: KeyValueOption[];
}

interface DeliveryAddress {
    customerAddress1: string;
    customerAddress2: string;
    customerAddress3: string;
    customerAddress4: string; // Country name
    customerCity: string;
    customerAddressPostCode: string;
    customerCountryCode: string;
    customerCompanyName: string;
}

// Initialization
function init() {

    // Setup order summary toggle
    document.getElementById('order-summary-toggle')?.addEventListener('click', function (e) {
        e.preventDefault();
        const osEl = document.getElementById('order-summary');
        const showOrderSummary = osEl?.classList.contains('hidden');
        osEl?.classList.toggle('hidden', !showOrderSummary);
        document.getElementById('order-summary-toggle__text-open')?.classList.toggle('hidden', !showOrderSummary);
        document.getElementById('order-summary-toggle__text-closed')?.classList.toggle('hidden', showOrderSummary);
    });

    const memberCountryElement = document.getElementById('memberCountry') as HTMLInputElement;
    const memberCountry = memberCountryElement?.value.trim() ?? '';

    // Display billing address regions if any
    document.querySelectorAll('select[name=\'billingAddress.Country\']').forEach(el => {
        const selectEl = el as HTMLSelectElement;

        /* Pre-select if we have a country from the page */
        if (memberCountry) {
            // match by option value first; fall back to visible text
            const match = Array.from(selectEl.options).find(
                o =>
                    o.value === memberCountry ||
                    o.textContent?.trim() === memberCountry
            );

            if (match)
            {
                // 1. Clear any earlier selected attributes
                Array.from(selectEl.options).forEach(o =>
                    o.removeAttribute('selected')
                );

                // 2. Update the JavaScript property (use index to avoid value-mismatch fallback to last option)
                selectEl.selectedIndex = Array.from(selectEl.options).indexOf(match);

                // 3. Reflect in the HTML attribute
                match.setAttribute('selected', 'selected');
            } else {
                // memberCountry set but no match — reset to placeholder
                Array.from(selectEl.options).forEach(o => o.removeAttribute('selected'));
                selectEl.selectedIndex = 0;
            }
        } else {
            // No memberCountry — guard against mobile browsers defaulting to last option
            // when no 'selected' attribute is present and first option is disabled
            const hasServerSelection = Array.from(selectEl.options).some(o => o.hasAttribute('selected'));
            if (!hasServerSelection) {
                selectEl.selectedIndex = 0;
            }
        }

        const h = () => { toggleShippingRequiredInputValidation(); showRegions('billing', JSON.parse(selectEl.selectedOptions[0]?.dataset.regions || '[]')); };
        el.addEventListener('change', h);
        h();
    });

    // Display shipping address regions if any
    document.querySelectorAll('select[name=\'shippingAddress.Country\']').forEach(el => {
        const selectEl = el as HTMLSelectElement;

        /* Pre-select if we have a country from the page */
        if (memberCountry) {
            const match = Array.from(selectEl.options).find(
                o =>
                    o.value === memberCountry ||
                    o.textContent?.trim() === memberCountry
            );

            if (match) {
                Array.from(selectEl.options).forEach(o =>
                    o.removeAttribute('selected')
                );
                selectEl.selectedIndex = Array.from(selectEl.options).indexOf(match);
                match.setAttribute('selected', 'selected');
            } else {
                // memberCountry set but no match — reset to placeholder
                Array.from(selectEl.options).forEach(o => o.removeAttribute('selected'));
                selectEl.selectedIndex = 0;
            }
        } else {
            // No memberCountry — guard against mobile browsers defaulting to last option
            // when no 'selected' attribute is present and first option is disabled
            const hasServerSelection = Array.from(selectEl.options).some(o => o.hasAttribute('selected'));
            if (!hasServerSelection) {
                selectEl.selectedIndex = 0;
            }
        }

        const h = () => { toggleShippingRequiredInputValidation(); showRegions('shipping', JSON.parse(selectEl.selectedOptions[0]?.dataset.regions || '[]')); };

        el.addEventListener('change', h);
        h();
    });

    // Sync shipping address visibility with the current checkbox state. The change handling itself
    // is delegated on `document` (see bottom of file) so it survives the checkout form being
    // re-rendered after init() — binding directly to the checkbox loses the listener when the
    // element is replaced by the page's frontend hydration.
    syncShippingInfo();

    // Update shippingOptionId based on selected shipping method
    document.querySelectorAll('input[name=shippingMethod]').forEach(el => {
        const h = () => {
            const shippingOptionEl: HTMLInputElement | null = document.querySelector('input[name=shippingOptionId]');
            if (!shippingOptionEl) {
                return;
            }

            shippingOptionEl.value = (<HTMLElement | null>document.querySelector('input[name=shippingMethod]:checked'))?.dataset.optionId || '';
        };

        el.addEventListener('change', h);
        if ((<HTMLInputElement>el).checked) h();
    });

    // Enable / disable continue button when accepting terms
    const acceptTermsEl = document.getElementById('accept-terms') as HTMLInputElement;
    const continueBtn = document.getElementById('continue') as HTMLButtonElement;
    if (acceptTermsEl) {
        acceptTermsEl.addEventListener('change', () => {
            continueBtn.disabled = !acceptTermsEl.checked;
        });
    } else {
        if (continueBtn)
        {
            continueBtn.disabled = false;
        }
    }

    populateDeliveryAddressSelect();
}

// Helper functions

// Show/hide the shipping address fieldset based on the "same as billing" checkbox, and keep the
// shipping required-field validation in sync. Reads the live DOM each call so it works regardless
// of which checkbox instance is currently mounted.
function syncShippingInfo() {
    const checkbox = document.querySelector('input[name=shippingSameAsBilling]') as HTMLInputElement | null;
    if (!checkbox) {
        return;
    }
    document.getElementById('shipping-info')?.classList.toggle('hidden', checkbox.checked);
    toggleShippingRequiredInputValidation();
}

function toggleShippingRequiredInputValidation() {
    const shippingSameAsBillingEl = document.querySelector('input[name=shippingSameAsBilling]') as HTMLInputElement;
    const shippingSameAsBilling = shippingSameAsBillingEl && shippingSameAsBillingEl.checked;
    document.querySelectorAll('#shipping-info [required]').forEach(el => {
        const htmlEl = el as HTMLInputElement;
        htmlEl.disabled = shippingSameAsBilling; // Disable any shipping required fields (this overrides the required validation)
    });
}

type SelectItem = {
    id: string,
    name: string,
}

function showRegions(addressType: string, regions: SelectItem[]) {
    const sl = document.querySelector('select[name=\'' + addressType + 'Address.Region\']') as HTMLSelectElement;
    const slVal = sl.dataset.value || '';

    sl.innerHTML = '';

    const hasRegions = regions.length > 0;
    if (hasRegions) {
        let containsValue = false;

        const opt = document.createElement('option');
        opt.value = '';
        opt.text = sl.dataset.placeholder || '';
        opt.disabled = true;
        opt.selected = true;
        sl.appendChild(opt);

        regions.forEach(function (itm) {
            const opt = document.createElement('option');
            opt.value = itm.id;
            opt.text = itm.name;
            sl.appendChild(opt);
            if (slVal && itm.id === slVal) {
                containsValue = true;
            }
        });

        if (containsValue) {
            sl.value = slVal;
        }
    }

    sl.required = hasRegions;
    sl.disabled = !hasRegions;
};

function formatDeliveryAddress(addr: DeliveryAddress): string {
    const parts = [addr.customerCompanyName, addr.customerAddress1, addr.customerAddress2, addr.customerCity, addr.customerAddress4]
        .map(p => (p || '').trim())
        .filter(p => !!p);
    if (addr.customerAddressPostCode?.trim()) {
        parts.push(addr.customerAddressPostCode.trim());
    }
    return parts.join(', ');
}

/*
    Populate shipping address form inputs when a saved delivery address is selected.
    Inputs targeted (by name: shippingAddress.Address1 .. Address4, shippingAddress.PostCode
    Additionally: address4 is treated as the country name and will select the matching option in shippingAddress.Country
*/
function applySelectedDeliveryAddress(selectEl: HTMLSelectElement) {
    const selected = selectEl.selectedOptions[0];
    const fields: { key: string; datasetKey: string; }[] = [
        { key: 'Line1', datasetKey: 'address1' },
        { key: 'Line2', datasetKey: 'address2' },
        { key: 'City', datasetKey: 'customerCity' },
        { key: 'Address4', datasetKey: 'address4' }, // country name will also map to country select
        { key: 'ZipCode', datasetKey: 'postcode' },
        { key: 'CountryCode', datasetKey: 'countryCode' },
        { key: 'Company', datasetKey: 'companyName' }
    ];

    const countrySelect = document.querySelector(`select[name='shippingAddress.Country']`) as HTMLSelectElement | null;

    if (!selected || !selected.dataset.address1) {
        fields.forEach(f => {
            const input = document.querySelector(`input[name='shippingAddress.${f.key}']`) as HTMLInputElement | null;
            if (input) {
                input.value = '';
                input.removeAttribute('readonly');
            }
        });
        if (countrySelect) countrySelect.removeAttribute('disabled');
        return;
    }

    fields.forEach(f => {
        const input = document.querySelector(`input[name='shippingAddress.${f.key}']`) as HTMLInputElement | null;
        if (input) {
            input.value = (selected.dataset as any)[f.datasetKey] || '';
            input.setAttribute('readonly', 'readonly');
        }
    });

    // Select the matching country option — try name first, fall back to ISO code
    const countryName = (selected.dataset.address4 || '').trim();
    const countryCode = (selected.dataset.countryCode || '').trim();
    if (countryName || countryCode) {
        if (countrySelect) {
            const options = Array.from(countrySelect.options);
            const match =
                (countryName && options.find(o => o.textContent?.trim().toLowerCase() === countryName.toLowerCase())) ||
                (countryCode && options.find(o => (o.dataset.code || '').trim().toLowerCase() === countryCode.toLowerCase()));
            if (match) {
                Array.from(countrySelect.options).forEach(o => o.removeAttribute('selected'));
                countrySelect.selectedIndex = Array.from(countrySelect.options).indexOf(match);
                match.setAttribute('selected', 'selected');
                countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
                // No matching country found — reset to placeholder (index 0) to avoid stale or wrong selection
                Array.from(countrySelect.options).forEach(o => o.removeAttribute('selected'));
                countrySelect.selectedIndex = 0;
                countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }
}

async function populateDeliveryAddressSelect() {
    try
    {        
        const selectElementLabel = document.getElementById('shippingDeliveryAddressesLabel') as HTMLElement;
        const selectElement = document.getElementById('shippingDeliveryAddresses') as HTMLSelectElement;
        if (!selectElement)
        {
            return;
        }

        const customerNumber = (document.getElementById('CN-X') as HTMLInputElement)?.value?.trim() || undefined;

        // If no customer number, hide the select element
        if (!customerNumber)
        {
            selectElement.removeAttribute('required');
            selectElement.style.display = 'none';
            selectElementLabel.style.display = 'none';
            return;
        }

        const payload = { customerNumber };

        const response = await fetch('/umbraco/api/datawarehouseapi/getdeliveryaddresses', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const data: { results: DeliveryAddress[] } = await response.json();
        const results = Array.isArray(data.results) ? data.results : [];

        // Clear existing options
        selectElement.innerHTML = '';

        if (results.length === 0) {
            const emptyOpt = document.createElement('option');
            emptyOpt.value = '';
            emptyOpt.textContent = 'No saved delivery addresses';
            emptyOpt.disabled = true;
            emptyOpt.selected = true;
            selectElement.appendChild(emptyOpt);
            // Ensure inputs are cleared
            applySelectedDeliveryAddress(selectElement);
            return;
        }

        // Placeholder
        const placeholderOpt = document.createElement('option');
        placeholderOpt.value = '';
        placeholderOpt.textContent = 'Select a saved address...';
        placeholderOpt.disabled = true;
        placeholderOpt.selected = true;
        selectElement.appendChild(placeholderOpt);

        const preSelectedValue = selectElement.dataset.value?.trim();

        results.forEach((addr, idx) => {
            const label = formatDeliveryAddress(addr) || `Address ${idx + 1}`;
            const optionElement = document.createElement('option');
            optionElement.value = label; // Could be replaced with an ID if available.
            optionElement.textContent = label;

            // Store raw fields for later use.
            optionElement.dataset.address1 = addr.customerAddress1 || '';
            optionElement.dataset.address2 = addr.customerAddress2 || '';
            optionElement.dataset.address4 = addr.customerAddress4 || ''; // country name
            optionElement.dataset.customerCity = addr.customerCity || '';
            optionElement.dataset.postcode = addr.customerAddressPostCode || '';
            optionElement.dataset.countryCode = addr.customerCountryCode || '';
            optionElement.dataset.companyName = addr.customerCompanyName || '';

            if (preSelectedValue && preSelectedValue === label) {
                optionElement.selected = true;
                placeholderOpt.selected = false;
            }

            selectElement.appendChild(optionElement);
        });

        // Attach change listener to populate inputs
        selectElement.addEventListener('change', () => applySelectedDeliveryAddress(selectElement));

        // Apply immediately if a pre-selected value exists
        applySelectedDeliveryAddress(selectElement);

    } catch (error) {
        console.error('Error fetching delivery addresses:', error);
    }
}

async function populateBusinessAreaSelect() {
    try {
        const response = await fetch('/umbraco/api/HubSpotApi/businessarea');
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const data: HubSpotKeyValueOptions = await response.json();
        const selectElement = document.getElementById('billingAddress.BusinessArea') as HTMLSelectElement;
        const selectedValue = (document.getElementById('memberBusinessArea') as HTMLInputElement)?.value;

        if (!selectElement) {
            return;
        }

        selectElement.innerHTML = '';

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select business area...';
        placeholder.disabled = true;
        placeholder.selected = true;
        selectElement.appendChild(placeholder);

        (data?.Options || []).forEach(option => {
            const optionElement = document.createElement('option');
            optionElement.value = option.Value;
            optionElement.textContent = option.Label;
            if (option.Value === selectedValue) {
                optionElement.selected = true;
                placeholder.selected = false;
            }
            selectElement.appendChild(optionElement);
        });

    } catch (error) {
        console.error('Error fetching data:', error);
    }
}

// The checkout form is rendered inside the Vue app (#app, mounted in main.ts). When Vue mounts it
// compiles and REPLACES that DOM subtree, which wipes any listeners/population this script applied
// beforehand. Since this `defer` script runs BEFORE main.ts, running init() now would bind to DOM
// nodes Vue then throws away (lost listeners, empty delivery-address select, reset country, etc.).
// main.ts dispatches `ucc:mounted` right after app.mount(), so we wait for that and bind to the
// final, re-rendered DOM. This works in both dev and prod regardless of when Vue finishes mounting.
let uccStarted = false;
function start() {
    if (uccStarted) {
        return;
    }
    uccStarted = true;
    init();
}

if (document.getElementById('app')) {
    // Vue app present: wait until it has mounted so we bind to the final DOM.
    window.addEventListener('ucc:mounted', start);
    // Safety net in case the app errors out and never dispatches the event.
    window.addEventListener('load', function () { setTimeout(start, 2000); });
} else {
    // No Vue app on this page — the DOM is already final.
    if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
}

// Re-run init when page is restored from bfcache (mobile back-button navigation).
window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
        init();
    }
});

// Delegated on `document` (never replaced) so the shipping toggle keeps working even if the
// checkout form is re-rendered again after mount.
document.addEventListener('change', function (e) {
    const target = e.target as HTMLInputElement | null;
    if (target && target.name === 'shippingSameAsBilling') {
        syncShippingInfo();
    }
});



